"use client";

import { useRef, type ReactNode } from "react";
import { AuthProvider } from "@/providers/AuthProvider";
import { I18nProvider } from "@/i18n";
import { useTerms } from "@/hooks/useTerms";
import { TermsModal } from "@/components/TermsModal";
import { TermsContext, type CheckAndPromptOptions } from "@/contexts/terms";
import type { TermsType } from "@/api/types";

function TermsProvider({ children }: { children: ReactNode }) {
  const terms = useTerms();
  const currentAddressRef = useRef<string | null>(null);

  const handleAccept = async () => {
    if (terms.modalTermsType && currentAddressRef.current) {
      await terms.accept(currentAddressRef.current, terms.modalTermsType);
    }
  };

  const wrappedCheckAndPrompt = async (
    walletAddress: string | undefined,
    termsType: TermsType,
    options?: CheckAndPromptOptions
  ) => {
    currentAddressRef.current = walletAddress ?? null;
    const checkAddress = options?.checkAsUser ? undefined : walletAddress;
    return terms.checkAndPrompt(checkAddress, termsType);
  };

  return (
    <TermsContext.Provider
      value={{
        checkAndPrompt: wrappedCheckAndPrompt,
        clearCache: terms.clearCache,
      }}
    >
      {children}
      <TermsModal
        isOpen={terms.showModal}
        termsType={terms.modalTermsType}
        isLoading={terms.isLoading}
        onAccept={handleAccept}
        onClose={terms.closeModal}
      />
    </TermsContext.Provider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <I18nProvider>
        <TermsProvider>{children}</TermsProvider>
      </I18nProvider>
    </AuthProvider>
  );
}
